import { useCallback, useEffect, useState } from 'react'
import { RefreshCw, AlertTriangle } from 'lucide-react'
import { api } from '@/lib/api'

/**
 * Sincronización JR2_OS — Sprint 15.
 *
 * Muestra el estado del adapter (mock/http), el último pull toga → stubs con
 * sus contadores por recurso, y un historial breve (últimos 10 SyncRun).
 * «Sincronizar ahora» dispara POST /v1/marketing/sync/run (202 asíncrono).
 * Mientras la página está montada, /status se pollea siempre: cada 10 s en
 * reposo (recoge syncs programados o lanzados desde otra pestaña) y cada 4 s
 * cuando hay un run en curso.
 */

interface ResourceCountersDto {
  fetched: number
  created: number
  updated: number
  skipped: number
  /** Omitidos por RGPD (stub anonimizado) — opcional: runs antiguos no lo traen. */
  skippedErased?: number
  conflicts: number
  deletedMarked: number
}

interface SyncCountersDto {
  serviceLines: ResourceCountersDto
  clients: ResourceCountersDto
  opportunities: ResourceCountersDto
  warnings: string[]
}

interface SyncRunDto {
  id: string
  status: 'RUNNING' | 'OK' | 'ERROR'
  trigger: 'MANUAL' | 'SCHEDULED'
  startedAt: string
  finishedAt: string | null
  countersJson: SyncCountersDto | null
  errorMessage: string | null
}

interface StatusDto {
  adapterMode: 'mock' | 'http'
  apiUrl: string | null
  intervalMin: number | null
  running: boolean
  lastRun: SyncRunDto | null
  runs: SyncRunDto[]
}

const RESOURCE_LABELS: Array<{ key: keyof Omit<SyncCountersDto, 'warnings'>; label: string }> = [
  { key: 'serviceLines', label: 'Líneas de servicio' },
  { key: 'clients', label: 'Clientes' },
  { key: 'opportunities', label: 'Oportunidades' },
]

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function durationSec(run: SyncRunDto): string {
  if (!run.finishedAt) return '…'
  const ms = new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
  return `${Math.max(0, Math.round(ms / 1000))}s`
}

function StatusPill({ status }: { status: SyncRunDto['status'] }) {
  const cls =
    status === 'OK'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'ERROR'
        ? 'bg-red-50 text-red-700'
        : 'bg-blueprint-50 text-blueprint-700'
  const label = status === 'OK' ? 'ok' : status === 'ERROR' ? 'error' : 'en curso'
  return <span className={`bp-pill ${cls}`}>{label}</span>
}

export function SyncPage() {
  const [status, setStatus] = useState<StatusDto | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [launching, setLaunching] = useState(false)

  const load = useCallback(async () => {
    try {
      const r = await api<StatusDto>('/v1/marketing/sync/status')
      setStatus(r)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // Poll de status mientras la página está montada: ligero cada 10 s en
  // reposo (así se ven llegar los syncs programados o lanzados desde otra
  // pestaña) y cada 4 s cuando hay un run en curso. Limpieza al desmontar.
  useEffect(() => {
    const t = setInterval(() => void load(), status?.running ? 4000 : 10_000)
    return () => clearInterval(t)
  }, [status?.running, load])

  async function runNow() {
    setErr(null)
    setLaunching(true)
    try {
      await api('/v1/marketing/sync/run', { method: 'POST' })
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setLaunching(false)
    }
  }

  const isMock = status?.adapterMode === 'mock'
  const counters = status?.lastRun?.countersJson ?? null

  return (
    <div className="p-6 max-w-4xl">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight">Sincronización JR2_OS</h1>
          <p className="text-xs font-mono text-ink-700/60">
            pull de solo lectura crm.jr2.es → stubs locales · identidad y pipeline desde toga · el
            dominio marketing jamás se sobreescribe
          </p>
        </div>
        <button
          data-testid="sync-run-button"
          onClick={runNow}
          disabled={launching || Boolean(status?.running) || isMock}
          className="bp-btn-primary disabled:opacity-50"
          title={isMock ? 'Adapter en modo mock: no hay JR2_OS del que sincronizar' : undefined}
        >
          <RefreshCw className={`size-3 ${status?.running ? 'animate-spin' : ''}`} />
          {status?.running ? 'Sincronizando…' : 'Sincronizar ahora'}
        </button>
      </header>

      {err && (
        <div className="mb-3 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {err}
        </div>
      )}

      {/* Estado del adapter */}
      <div className="bp-card p-4 mb-4">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-2">
          Adapter
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-700/60">Modo</span>
            <span
              data-testid="sync-mode"
              className={`bp-pill ${isMock ? 'bg-ink-900/10 text-ink-700/70' : 'bg-blueprint-50 text-blueprint-700'}`}
            >
              {status?.adapterMode ?? '…'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-700/60">API</span>
            <span className="font-mono text-xs">{status?.apiUrl ?? '—'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-700/60">Sync automático</span>
            <span className="font-mono text-xs">
              {status?.intervalMin ? `cada ${status.intervalMin} min` : 'desactivado'}
            </span>
          </div>
        </div>
        {isMock && (
          <p className="mt-2 text-xs text-ink-700/60">
            En modo mock los stubs son datos locales de desarrollo. Configura{' '}
            <code className="font-mono">JR2OS_ADAPTER=http</code> con el usuario de servicio para
            sincronizar contra crm.jr2.es.
          </p>
        )}
      </div>

      {/* Último sync */}
      <div className="bp-card p-4 mb-4">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-2">
          Última sincronización
        </div>
        {!status?.lastRun ? (
          <p className="text-xs text-ink-700/50">Todavía no se ha ejecutado ninguna.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm mb-3">
              <StatusPill status={status.lastRun.status} />
              <span className="font-mono text-xs">{fmtDate(status.lastRun.startedAt)}</span>
              <span className="text-xs text-ink-700/60">
                {status.lastRun.trigger === 'MANUAL' ? 'manual' : 'programada'} ·{' '}
                {durationSec(status.lastRun)}
              </span>
            </div>

            {status.lastRun.errorMessage && (
              <div className="mb-3 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                {status.lastRun.errorMessage}
              </div>
            )}

            {counters && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
                    <tr>
                      <th className="px-3 py-2 text-left">Recurso</th>
                      <th className="px-3 py-2 text-right">Leídos</th>
                      <th className="px-3 py-2 text-right">Creados</th>
                      <th className="px-3 py-2 text-right">Actualizados</th>
                      <th className="px-3 py-2 text-right">Omitidos</th>
                      <th className="px-3 py-2 text-right">Omitidos RGPD</th>
                      <th className="px-3 py-2 text-right">Conflictos</th>
                      <th className="px-3 py-2 text-right">Borrados en origen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RESOURCE_LABELS.map(({ key, label }) => {
                      const c = counters[key]
                      return (
                        <tr key={key} className="border-t border-ink-900/10">
                          <td className="px-3 py-2">{label}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">{c.fetched}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">{c.created}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">{c.updated}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">{c.skipped}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">
                            {c.skippedErased ?? 0}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-mono text-xs ${c.conflicts > 0 ? 'text-amber-700' : ''}`}
                          >
                            {c.conflicts}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-xs">
                            {c.deletedMarked}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {counters && counters.warnings.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1">
                  <AlertTriangle className="size-3" /> Avisos ({counters.warnings.length})
                </div>
                <ul className="text-xs text-ink-700/70 space-y-0.5 max-h-48 overflow-y-auto">
                  {counters.warnings.map((w, i) => (
                    <li key={i} className="font-mono">
                      · {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {/* Historial */}
      <div className="bp-card overflow-hidden">
        <div className="px-4 pt-3 text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
          Historial (últimas 10)
        </div>
        <table className="w-full text-sm mt-2">
          <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
            <tr>
              <th className="px-3 py-2 text-left">Inicio</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Origen</th>
              <th className="px-3 py-2 text-right">Duración</th>
              <th className="px-3 py-2 text-right">Clientes</th>
              <th className="px-3 py-2 text-right">Oportunidades</th>
            </tr>
          </thead>
          <tbody>
            {(status?.runs ?? []).map((r) => {
              const c = r.countersJson
              return (
                <tr key={r.id} className="border-t border-ink-900/10">
                  <td className="px-3 py-2 font-mono text-xs">{fmtDate(r.startedAt)}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-700/70">
                    {r.trigger === 'MANUAL' ? 'manual' : 'programada'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{durationSec(r)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {c ? `${c.clients.created}+ ${c.clients.updated}~` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {c ? `${c.opportunities.created}+ ${c.opportunities.updated}~` : '—'}
                  </td>
                </tr>
              )
            })}
            {(status?.runs ?? []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-xs text-ink-700/50">
                  Sin sincronizaciones todavía.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
